export function stripCgiHeaders(output) {
  const boundary = output.search(/\r?\n\r?\n/);
  return boundary === -1 ? output : output.slice(boundary).replace(/^\r?\n\r?\n/, "");
}

function text(value) {
  return String(value || "").replace(/\s+/g, " ").trim();
}

function tableRows(table) {
  return Array.from(table.querySelectorAll("tr"))
    .map((row) => ({
      header: row.querySelector("th") !== null,
      cells: Array.from(row.querySelectorAll(":scope > th, :scope > td")).map((cell) => text(cell.textContent)),
    }))
    .filter((row) => row.cells.some(Boolean));
}

function sectionTables(heading) {
  const tables = [];
  let sibling = heading.nextElementSibling;
  while (sibling && !/^H[1-3]$/.test(sibling.tagName)) {
    if (sibling.tagName === "TABLE") tables.push({ rows: tableRows(sibling) });
    sibling = sibling.nextElementSibling;
  }
  return tables.filter(({ rows }) => rows.length > 0);
}

function errorAfter(heading) {
  const messages = [];
  let node = heading.nextSibling;
  while (node) {
    if (node.nodeType === Node.ELEMENT_NODE && /^H[1-6]$/.test(node.tagName)) break;
    const value = text(node.textContent);
    if (value) messages.push(value);
    node = node.nextSibling;
  }
  return messages.join(" ") || "The legacy calculation engine rejected these inputs.";
}

export function parseCgiResponse(output) {
  const html = stripCgiHeaders(output);
  const document = new DOMParser().parseFromString(html, "text/html");
  const headings = Array.from(document.querySelectorAll("h2"));
  const errorHeading = headings.find((heading) => text(heading.textContent).toLowerCase() === "error");

  if (errorHeading) {
    return { title: "Calculation error", error: errorAfter(errorHeading), sections: [] };
  }

  const sections = Array.from(document.querySelectorAll("h3")).map((heading) => ({
    title: text(heading.textContent),
    tables: sectionTables(heading),
  })).filter((section) => section.tables.length > 0);

  return {
    title: text(document.querySelector("title")?.textContent) || "Calculation result",
    error: null,
    sections,
  };
}

export function encodeCgiParameters(formData, title) {
  const parameters = new URLSearchParams(formData);
  parameters.set("setup_ttl", `${title} Results`);
  parameters.set("setup_txt", "#E0E0E0");
  parameters.set("setup_bgc", "#111111");
  parameters.set("setup_lnk", "#5B9BD5");
  parameters.set("setup_vlk", "#A4A4A4");
  parameters.set("setup_bkg", "");
  parameters.set("setup_frm", "spa");
  return parameters.toString();
}

export async function executeCgi(moduleName, body, moduleBase = "/wasm") {
  const output = [];
  const errors = [];
  const bytes = new TextEncoder().encode(body);
  let offset = 0;
  const moduleUrl = `${moduleBase}/${moduleName}.js`;
  const { default: createModule } = await import(/* @vite-ignore */ moduleUrl);
  const module = await createModule({
    noInitialRun: true,
    locateFile: (path) => `${moduleBase}/${path}`,
    preRun: (instance) => {
      instance.ENV.CONTENT_TYPE = "application/x-www-form-urlencoded";
      instance.ENV.CONTENT_LENGTH = String(bytes.length);
      instance.ENV.REQUEST_METHOD = "POST";
    },
    stdin: () => offset < bytes.length ? bytes[offset++] : null,
    stdout: (value) => output.push(String.fromCharCode(value)),
    stderr: (value) => errors.push(String.fromCharCode(value)),
  });

  module.callMain([]);
  if (output.length === 0 && errors.length > 0) throw new Error(errors.join(""));
  return output.join("");
}
